import type { FC } from 'react';

import { useMemo, memo } from 'react';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

import type {
  ImageLink,
  Images1,
} from '~/amplienceTypes/partials/exported/responsive-image-schema';
import { AiWatermark } from '~/components/shared/aiWatermark/AiWatermark';
import { FesLink } from '~/components/shared/core/link/FesLink';
import { media } from '~/components/shared/core/styles';
import { ImageSource } from '~/components/unmanaged/partials/ImageSources';
import { FIT_NAME_VARIABLE } from '~/utilities/constants/amplienceVariables';

const S = {
  Wrapper: styled.div`
    position: relative;
    aspect-ratio: 0.75 / 1;
  `,

  Link: styled(FesLink)`
    display: block;
    overflow: hidden;
    height: 100%;
    background: linear-gradient(0deg, rgb(0 0 0 / 40%) 0%, rgb(0 0 0 / 0%) 50%);
    transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(0deg, rgb(0 0 0 / 40%) 0%, rgb(0 0 0 / 0%) 50%);
      z-index: 2;
      opacity: 1;
      transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    }

    &:hover,
    &:focus {
      &::before {
        opacity: 0.5;
      }

      img {
        transform: scale(1.02);
      }
    }
  `,

  Label: styled.p`
    font-size: ${({ theme }) => theme.font.size['6']};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: ${({ theme }) => theme.colors.neutral['0']};
    font-weight: ${({ theme }) => theme.variables.font.weight.medium};
    text-align: center;
    text-transform: uppercase;
    z-index: 2;

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      font-size: ${({ theme }) => theme.font.size['7']};
    }
  `,

  Image: styled.img`
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    object-fit: cover;
    object-position: var(--contentBlock-image-position, 50% 50%);
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
};

interface FitTileProps {
  url: string;
  image: ImageLink;
  code?: string | null;
  setTileWidth: (width: number) => void;
  tileAriaLabel: string;
  // Roving focus props
  ref?: (el: HTMLElement | null) => void;
  tabIndex?: number;
}

const FitTile: FC<FitTileProps> = ({
  code,
  url,
  image,
  setTileWidth,
  tileAriaLabel,
  ref: rovingFocusRef,
  tabIndex = 0,
}) => {
  const ariaLabel = useMemo(
    () => tileAriaLabel?.replaceAll(FIT_NAME_VARIABLE, code || ''),
    [code, tileAriaLabel]
  );

  const imageConfig: Images1 = useMemo(() => ({ mobile: image }), [image]);

  return (
    <S.Wrapper
      ref={(e: HTMLDivElement) => {
        setTileWidth(Math.ceil(e?.getBoundingClientRect().width || 0));
      }}
    >
      <S.Link
        href={url}
        aria-label={ariaLabel}
        tabIndex={tabIndex}
        ref={(e: HTMLAnchorElement | null) => {
          rovingFocusRef?.(e);
        }}
      >
        <picture>
          <ImageSource images={imageConfig} />
          <S.Image decoding="async" alt="" role="presentation" aria-hidden="true" />
        </picture>
        <AiWatermark imageLinks={[image]} className="ai-icon-container" />
        <S.Label>{code}</S.Label>
      </S.Link>
    </S.Wrapper>
  );
};

export default memo(FitTile, isEqual);
